import { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Plus, Trash2, Shield } from 'lucide-react';

function SettingsManagement() {
  const [allowedIps, setAllowedIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/ip-restrictions`);
      setAllowedIps(response.data.allowed_ips || ['0.0.0.0']);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const addIp = () => {
    if (!newIp.trim()) {
      toast.error('Please enter a valid IP address');
      return;
    }
    if (allowedIps.includes(newIp.trim())) {
      toast.error('IP address already exists');
      return;
    }
    setAllowedIps([...allowedIps, newIp.trim()]);
    setNewIp('');
  };

  const removeIp = (ip) => {
    if (allowedIps.length === 1) {
      toast.error('Must have at least one IP address');
      return;
    }
    setAllowedIps(allowedIps.filter(i => i !== ip));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ip-restrictions`, { allowed_ips: allowedIps });
      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-sm text-gray-600 mt-1">Configure application settings</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-6">
        <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
          <Shield className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-semibold text-gray-900">IP Address Restrictions</h3>
            <p className="text-sm text-gray-600">Control which IP addresses can access staff features</p>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Allowed IP Addresses</Label>
          <p className="text-xs text-gray-500">Enter 0.0.0.0 to allow all IP addresses</p>
          
          <div className="flex gap-2">
            <Input
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              placeholder="Enter IP address (e.g., 192.168.1.1 or 0.0.0.0)"
              onKeyPress={(e) => e.key === 'Enter' && addIp()}
            />
            <Button onClick={addIp} className="bg-primary hover:bg-primary-light">
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>

          <div className="space-y-2 mt-4">
            {allowedIps.map((ip) => (
              <div key={ip} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <span className="text-sm font-mono text-gray-900">{ip}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeIp(ip)}
                  disabled={allowedIps.length === 1}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button onClick={saveSettings} disabled={saving} className="bg-primary hover:bg-primary-light">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SettingsManagement;